
import mixpanel from 'mixpanel-browser';
import * as Sentry from '@sentry/react';

const staticMeta = {
  app_name: "TastermonialLab"
}

// Initialize Mixpanel with your project token
// Replace this with your actual Mixpanel token
const token = import.meta.env.VITE_MIXPANEL_TOKEN || '4bc930e0f709c27455f7aafd3d314c97';
mixpanel.init(token, {
  debug: import.meta.env.DEV,
  track_pageview: true,
  persistence: 'localStorage',
  ip: true, // Enable IP tracking
  property_blacklist: ["$current_url", "$initial_referrer", "$referrer"], // Don't track these properties
  loaded: (mixpanel) => {
    // Set the app name for all events as "EarlyBird"
    mixpanel.register({
      ...staticMeta
    });
  }
});

// Helper functions for tracking
const MixpanelService = {
  /**
   * Track a page view
   * @param pageName - Name of the page being viewed
   */
  trackPageView: (pageName: string) => {
    mixpanel.track('Page View', {
      page: pageName,
      ...staticMeta
    });
  },

  /**
   * Track a button click
   * @param buttonName - Name or identifier of the button
   * @param properties - Additional properties to track
   */
  trackButtonClick: (buttonName: string, properties: Record<string, any> = {}) => {
    mixpanel.track('Button Click', {
      button: buttonName,
      ...staticMeta,
      ...properties
    });
  },

  /**
   * Track a user action
   * @param actionName - Name of the action
   * @param properties - Additional properties to track
   */
  trackAction: (actionName: string, properties: Record<string, any> = {}) => {
    mixpanel.track(actionName, {
      ...staticMeta,
      ...properties
    });
  },

  /**
   * Set user properties
   * @param properties - User properties to set
   */
  setUserProperties: (properties: Record<string, any>) => {
    mixpanel.people.set({
      ...staticMeta,
      ...properties
    });
  },

  /**
   * Set user ID for tracking
   * @param userId - User ID
   */
  identify: (userId: string) => {
    mixpanel.identify(userId);
  },

  /**
   * Track user location and device information
   */
  trackUserInfo: async () => {
    try {
      // Get location information from IP using a free API
      const response = await fetch('https://ipapi.co/json/');
      const data = await response.json();

      // Set user location properties
      if (data) {
        const locationData = {
          city: data.city,
          region: data.region,
          country: data.country_name,
          ip: data.ip,
          timezone: data.timezone
        };

        // Add browser and device information
        const userAgentData = {
          browser: navigator.userAgent,
          language: navigator.language,
          screenWidth: window.screen.width,
          screenHeight: window.screen.height,
          referrer: document.referrer || 'direct'
        };

        // Add to a single track event
        mixpanel.track('User Info', {
          ...staticMeta,
          ...locationData,
          ...userAgentData
        });

        // Also set as user properties
        MixpanelService.setUserProperties({
          ...locationData,
          ...userAgentData,
          $last_seen: new Date()
        });
      }
    } catch (error) {
      Sentry.captureException(error);
    }
  }
};

export default MixpanelService;

